import { useState, useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Elements } from '@stripe/react-stripe-js';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { PaymentMethodForm } from '@/components/billing/PaymentMethodForm';
import {
  useSubscription,
  useStoredPlan,
  usePlans,
  usePaymentMethods,
  formatPrice,
} from '@/hooks/useSubscription';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/components/ui/use-toast';
import { getStripe } from '@/lib/stripe';
import { CreditCard, AlertCircle, Check, Loader2 } from 'lucide-react';

type PaymentStep = 'loading' | 'add_payment' | 'confirm' | 'processing' | 'success';

export function CompletePaymentPage() {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { subscription, createSetupIntent, createSubscription, isCreateLoading } = useSubscription();
  const { storedPlan, storedPeriod, clearStoredPlan } = useStoredPlan();
  const { getPlan } = usePlans();
  const { paymentMethods, defaultPaymentMethod, savePaymentMethod, isSaveLoading } = usePaymentMethods();

  const [step, setStep] = useState<PaymentStep>('loading');
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [stripeLoaded, setStripeLoaded] = useState(false);

  // Determine the plan to subscribe to
  const planType = storedPlan || subscription?.plan_type || 'solo';
  const billingPeriod = storedPeriod || 'monthly';
  const selectedPlan = getPlan(planType, billingPeriod);

  // Load Stripe
  useEffect(() => {
    getStripe().then((stripe) => {
      if (stripe) {
        setStripeLoaded(true);
      }
    });
  }, []);

  // Set initial step based on existing payment methods
  useEffect(() => {
    if (paymentMethods !== undefined) {
      if (defaultPaymentMethod) {
        setStep('confirm');
      } else {
        setStep('add_payment');
      }
    }
  }, [paymentMethods, defaultPaymentMethod]);

  // Set the selected plan ID when plan data loads
  useEffect(() => {
    if (selectedPlan) {
      setSelectedPlanId(selectedPlan.id);
    }
  }, [selectedPlan]);

  // Create setup intent when entering add_payment step
  useEffect(() => {
    if (step === 'add_payment' && !clientSecret) {
      createSetupIntent()
        .then((result) => {
          if (result?.clientSecret) {
            setClientSecret(result.clientSecret);
          }
        })
        .catch((error) => {
          toast({
            title: 'Error',
            description: error.message || 'Failed to initialize payment',
            variant: 'destructive',
          });
        });
    }
  }, [step, clientSecret, createSetupIntent]);

  const handlePaymentMethodSuccess = async (paymentMethodId: string) => {
    try {
      // Save the payment method
      await savePaymentMethod({ paymentMethodId, setAsDefault: true });

      // Move to confirm step
      setStep('confirm');

      toast({
        title: 'Payment method saved',
        description: 'Your card has been saved successfully.',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to save payment method',
        variant: 'destructive',
      });
    }
  };

  const handleSubscribe = async () => {
    if (!selectedPlanId || !defaultPaymentMethod) {
      toast({
        title: 'Error',
        description: 'Please add a payment method first.',
        variant: 'destructive',
      });
      return;
    }

    setStep('processing');

    try {
      await createSubscription({
        planId: selectedPlanId,
        paymentMethodId: defaultPaymentMethod.stripe_payment_method_id,
      });

      setStep('success');
      clearStoredPlan();

      toast({
        title: 'Subscription activated',
        description: 'Welcome to Sapling! Your subscription is now active.',
      });

      // Redirect to dashboard after a short delay
      setTimeout(() => {
        navigate({ to: '/app/dashboard' });
      }, 2000);
    } catch (error) {
      setStep('confirm');
      toast({
        title: 'Subscription failed',
        description: error instanceof Error ? error.message : 'Failed to create subscription',
        variant: 'destructive',
      });
    }
  };

  const handleChangePlan = () => {
    navigate({ to: '/app/plan-selection' });
  };

  const handleLogout = async () => {
    await signOut();
  };

  const handleAddNewCard = () => {
    setClientSecret(null);
    setStep('add_payment');
  };

  // Render loading state
  if (!stripeLoaded || step === 'loading') {
    return (
      <div className="flex min-h-screen flex-col">
        <Header />
        <main className="flex flex-1 items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </main>
        <Footer />
      </div>
    );
  }

  // Render success state
  if (step === 'success') {
    return (
      <div className="flex min-h-screen flex-col">
        <Header />
        <main className="flex flex-1 items-center justify-center py-12">
          <div className="container">
            <div className="mx-auto max-w-md">
              <div className="rounded-modal border bg-card p-8 text-center">
                <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-sapling-primary-100/10">
                  <Check className="h-8 w-8 text-sapling-primary-100" />
                </div>

                <h1 className="font-display text-2xl font-bold">
                  You're All Set!
                </h1>

                <p className="mt-4 text-muted-foreground">
                  Your subscription is now active. Redirecting to dashboard...
                </p>

                <div className="mt-6">
                  <Loader2 className="mx-auto h-6 w-6 animate-spin text-sapling-primary-100" />
                </div>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col">
      <Header />

      <main className="flex flex-1 items-center justify-center py-12">
        <div className="container">
          <div className="mx-auto max-w-md">
            <div className="rounded-modal border bg-card p-8">
              <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-sapling-warning-100/10">
                <AlertCircle className="h-8 w-8 text-sapling-warning-100" />
              </div>

              <h1 className="text-center font-display text-2xl font-bold">
                Complete Your Payment
              </h1>

              <p className="mt-4 text-center text-muted-foreground">
                Your account is set up. Add a payment method to activate your subscription.
              </p>

              {/* Selected Plan Display */}
              {selectedPlan && (
                <div className="mt-6 rounded-btn bg-muted p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Selected Plan</p>
                      <p className="font-display text-lg font-semibold">
                        {selectedPlan.plan_name}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-display text-lg font-semibold">
                        {formatPrice(selectedPlan.price)}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        /{billingPeriod === 'yearly' ? 'year' : 'month'}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Payment Method Section */}
              <div className="mt-6">
                {step === 'add_payment' && clientSecret ? (
                  <Elements
                    stripe={getStripe()}
                    options={{
                      clientSecret,
                      appearance: {
                        theme: 'stripe',
                        variables: {
                          colorPrimary: '#22C55E',
                          borderRadius: '8px',
                        },
                      },
                    }}
                  >
                    <PaymentMethodForm
                      clientSecret={clientSecret}
                      onSuccess={handlePaymentMethodSuccess}
                      onCancel={defaultPaymentMethod ? () => setStep('confirm') : undefined}
                      submitLabel="Save Card"
                      loading={isSaveLoading}
                    />
                  </Elements>
                ) : step === 'confirm' && defaultPaymentMethod ? (
                  <div className="space-y-4">
                    <div>
                      <p className="mb-2 text-sm font-medium">Payment Method</p>
                      <div className="flex items-center justify-between rounded-btn border bg-muted/50 p-3">
                        <div className="flex items-center gap-3">
                          <CreditCard className="h-5 w-5 text-muted-foreground" />
                          <span>
                            {defaultPaymentMethod.card_brand} ····{defaultPaymentMethod.card_last4}
                          </span>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleAddNewCard}
                        >
                          Change
                        </Button>
                      </div>
                    </div>

                    <Button
                      onClick={handleSubscribe}
                      loading={isCreateLoading}
                      className="w-full"
                    >
                      <CreditCard className="mr-2 h-4 w-4" />
                      Subscribe Now
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                )}
              </div>

              {/* Change Plan Link */}
              <div className="mt-6">
                <Button
                  variant="outline"
                  onClick={handleChangePlan}
                  disabled={isCreateLoading || step === 'processing'}
                  className="w-full"
                >
                  Change Plan
                </Button>
              </div>

              {user && (
                <p className="mt-6 text-center text-sm text-muted-foreground">
                  Signed in as {user.email}
                  {' · '}
                  <button
                    onClick={handleLogout}
                    className="text-sapling-primary-200 hover:text-sapling-primary-100"
                  >
                    Sign out
                  </button>
                </p>
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
